// Where each learning agent's pages live (docs/Y2026-Q3/design-project-agent-team-runs.md
// section 2.3): the project team (Manager, Grid, ...) under one roof. Every link is
// built here, so a move is one edit rather than a string template at every call site.

export const PROJECT_AGENTS = "/agents/project-agents";

const enc = encodeURIComponent;

type RunKind = "scheduled" | "conversations";

// A run's page, or the list it sits in when ``runId`` is omitted. The project
// team's runs are the team's, since its daily run is one run.
export const runPath = (kind: RunKind, runId?: string | null): string => {
  const base = `${PROJECT_AGENTS}/${kind}`;
  return runId ? `${base}/${enc(runId)}` : base;
};

// A project's conversation list, and one conversation in it.
export const projectConversationsPath = (projectId: string): string =>
  `/projects/${enc(projectId)}/learning-agents/conversations`;

export const projectConversationPath = (projectId: string, runId: string): string =>
  `${projectConversationsPath(projectId)}/${enc(runId)}`;

// A project's agent state, or one agent's document in it.
export const projectAgentStatePath = (projectId: string, agent?: string): string => {
  const base = `/projects/${enc(projectId)}/learning-agents/state`;
  return agent ? `${base}/${enc(agent)}` : base;
};

// The project's briefing: what its latest scheduled pass wrote, decisions and emails inline.
export const projectBriefingPath = (projectId: string): string =>
  `/projects/${enc(projectId)}/status/briefing`;

// The briefing's history, and one version of it as it was built.
export const projectStatusHistoryPath = (projectId: string, version?: number): string => {
  const base = `/projects/${enc(projectId)}/status-history`;
  return version ? `${base}/${version}` : base;
};

// A project's own page: its status tab, the one a site's name links to.
export const projectStatusPath = (projectId: string): string =>
  `/projects/${enc(projectId)}/status`;

// A specialist's own tab on a project: its status row and its unsent emails.
export const projectSpecialistPath = (projectId: string, agent: string): string =>
  `${projectStatusPath(projectId)}/${enc(agent)}`;

// One document's block on a specialist's "Documented obligations" card.
export const documentClausesPath = (projectId: string, agent: string, documentId: string): string =>
  `${projectSpecialistPath(projectId, agent)}#${enc(documentClauseAnchor(documentId))}`;

export const documentClauseAnchor = (documentId: string): string => `obligations-${documentId}`;

// A project's scheduled runs, and one run cut down to that project.
export const projectScheduledPath = (projectId: string, runId?: string | null): string => {
  const base = `/projects/${enc(projectId)}/learning-agents/scheduled`;
  return runId ? `${base}/${enc(runId)}` : base;
};

// A run's own page: a scheduled run on the team's scheduled tab, a project conversation
// under its project, and an agent-level conversation under the team.
export const statefulRunPage = (run: {
  run_id: string;
  kind: string;
  context_project_id?: string | null;
}): string => {
  if (run.kind === "scheduled" || run.kind === "learning_review") {
    return runPath("scheduled", run.run_id);
  }
  if (run.context_project_id) {
    return projectConversationPath(run.context_project_id, run.run_id);
  }
  return runPath("conversations", run.run_id);
};

// A link to a run known only by its id, such as the run that wrote a state version: the
// page looks the run up and opens it where it lives. Outside the team's tabbed layout,
// which sends any path that is not one of its tabs to its landing tab.
export const runLinkPath = (runId: string): string => `/agents/runs/${enc(runId)}`;

export const outputsPath = (agent: string, page: "portfolio"): string =>
  `${PROJECT_AGENTS}/outputs/${enc(agent)}/${page}`;

export const agentPagePath = (agent: string, page: "state" | "prompt"): string =>
  `${PROJECT_AGENTS}/${enc(agent)}/${page}`;

// An old per-agent path at its new home. ``rest`` is what followed
// /agents/<agent>/. Slack messages and bookmarks link to the old shape, so
// every one of them resolves rather than 404ing.
export const movedProjectAgentPath = (
  agent: string,
  isManager: boolean,
  rest: string,
): string => {
  const [head = "", sub = "", id = ""] = rest.split("/").filter(Boolean);
  if (head === "state" || head === "prompt") return `${PROJECT_AGENTS}/${enc(agent)}/${rest}`;
  if (head === "digest" && !isManager) return outputsPath(agent, "portfolio");
  if (head === "runs") {
    // runs/chats is the Conversations tab's older name.
    const kind: RunKind = sub === "scheduled" ? "scheduled" : "conversations";
    return runPath(kind, id ? decodeURIComponent(id) : null);
  }
  return isManager ? runPath("conversations") : outputsPath(agent, "portfolio");
};

// A document's clause page: under the project it was uploaded to, or under Global data.
// A Global data file's page opened under a project redirects to its Global data address.
export const registerPath = (documentId: string, projectId?: string | null): string =>
  projectId
    ? `/projects/${enc(projectId)}/files/register/${enc(documentId)}`
    : `/global-data/registers/${enc(documentId)}`;

// The clause page of a register: a Global data file's under Global data wherever it is
// reached from.
export const clausePagePath = (
  register: { document_id: string; document_kind: "upload" | "global" },
  projectId: string | null,
): string =>
  registerPath(register.document_id, register.document_kind === "upload" ? projectId : null);

// Which projects each optional specialist works, across the team.
export const teamSettingsPath = `${PROJECT_AGENTS}/settings`;
